import { Link } from "react-router";
import { money } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import type { ListFilter } from "../../../model/rechnungsliste";
import type { KennzahlenJeWaehrung } from "./kennzahlen-model";

export type { KennzahlenJeWaehrung };

/**
 * Kennzahlen über der Rechnungsliste (R-17, Umbauten 03.11.2026): statt einer Zahlenwand in kleiner Schrift vier Kacheln je
 * Währung, jede ein Klickziel auf die passende Ansicht: Ausstehend (offene Forderungen), Überfällig (nur Forderungen, wie die
 * Kachel der Übersicht), Zu prüfen (Handlungsbedarf), Auszahlungen an Creator (Seite Auszahlungen). Die Kachel der gewählten
 * Ansicht ist markiert.
 * Prüf-Chat Nachtest (03.11.2026): die Währung der Agentur steht immer zuerst (Reihenfolge aus rechnungsKennzahlen), jede
 * Fremdwährung als eigene, sichtbare Zeile darunter und nur mit ihren Kacheln, die einen Wert haben; die Auszahlungskachel nennt
 * die Zahl der offenen Creator-Rechnungen, damit die Summe zu den Filterzählern „Offen“ und „Überfällig“ passt.
 */
export function RechnungsKennzahlen({ kennzahlen, filter, nurForderungen, onFilter }: {
  kennzahlen: readonly (readonly [string, KennzahlenJeWaehrung])[];
  filter: ListFilter;
  nurForderungen: boolean;
  onFilter: (f: ListFilter, nurForderungen: boolean) => void;
}) {
  if (!kennzahlen.length) return null;
  const kachel = (key: string, label: string, wert: string, neben: string | null, aktiv: boolean, onClick: () => void, ton?: "error") => (
    <button key={key} type="button" className={`tk-umbau-kz-kachel${ton ? ` tk-umbau-kz-${ton}` : ""}`} aria-pressed={aktiv} onClick={onClick}>
      <small>{label}</small>
      <strong className="num">{wert}</strong>
      {neben && <small className="tk-umbau-kz-neben">{neben}</small>}
    </button>
  );
  // K-409 („Kennzahlen ohne Nullwerte“): die Hauptwährung zeigt ihre drei Kacheln immer (0 überfällig ist eine Aussage), weitere
  // Währungen nur die Kacheln mit Wert
  const reihe = ([cur, e]: readonly [string, KennzahlenJeWaehrung], erste: boolean) => (
    <div key={cur} className={`tk-umbau-kz-reihe${erste ? "" : " tk-umbau-kz-fremd"}`} role="group" aria-label={t(`Kennzahlen ${cur}`, `Figures ${cur}`)}>
      {!erste && <span className="tk-umbau-kz-waehrung">{cur}</span>}
      {(erste || e.receivable > 0 || e.open > 0) && kachel("offen", t("Ausstehend", "Outstanding"), money(e.receivable, cur), e.open === 1 ? t("1 offene Rechnung", "1 open invoice") : t(`${e.open} offene Rechnungen`, `${e.open} open invoices`),
        filter === "offen", () => onFilter("offen", false))}
      {(erste || e.overdue > 0) && kachel("ueberfaellig", t("Überfällig", "Overdue"), money(e.overdue, cur), e.overdueCount === 1 ? t("1 Rechnung", "1 invoice") : t(`${e.overdueCount} Rechnungen`, `${e.overdueCount} invoices`),
        filter === "ueberfaellig" && nurForderungen, () => onFilter("ueberfaellig", true), e.overdue > 0 ? "error" : undefined)}
      {(erste || e.attention > 0) && kachel("handlung", t("Zu prüfen", "To review"), String(e.attention), e.attention === 1 ? t("braucht Aufmerksamkeit", "needs attention") : t("brauchen Aufmerksamkeit", "need attention"),
        filter === "handlung", () => onFilter("handlung", false))}
      {(e.payout > 0 || e.payoutCount > 0) && (
        <Link key="payout" className="tk-umbau-kz-kachel" to="/agentur/auszahlungen">
          <small>{t("Auszahlungen an Creator", "Payouts to creators")}</small>
          <strong className="num">{money(e.payout, cur)}</strong>
          <small className="tk-umbau-kz-neben">{e.payoutCount === 1 ? t("1 offen, zur Seite Auszahlungen", "1 open, to the payouts page") : t(`${e.payoutCount} offen, zur Seite Auszahlungen`, `${e.payoutCount} open, to the payouts page`)}</small>
        </Link>
      )}
    </div>
  );
  return (
    <div className="tk-inv-kennzahlen tk-umbau-kz" role="status">
      {kennzahlen.map((k, i) => reihe(k, i === 0))}
    </div>
  );
}
